import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { contentMap, EmptyStateContent } from './empty-state.map';
import { StorageService } from '@web-frontend/shared/services';
import { USER_LANG } from '@tacliatech/types';

@Component({
  selector: 'empty-state',
  templateUrl: './empty-state.component.html',
  styleUrls: ['./empty-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmptyStateComponent implements OnInit {
  @Input()
  contentType: string;

  content: EmptyStateContent;

  @Output()
  firstButtonClick = new EventEmitter();

  @Output()
  secondButtonClick = new EventEmitter();

  isLoading = false;
  cookiesAccepted = false;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.draw();
  }

  constructor(private readonly changeDetectionRef: ChangeDetectorRef) {}

  get isMobile(): boolean {
    return window.innerWidth < 768;
  }

  get buttonSize(): string {
    return this.isMobile ? 'small' : 'medium';
  }

  get lang(): string {
    return StorageService.GetItem(USER_LANG)?.toLowerCase() || 'es';
  }

  get shouldShowVideo(): boolean {
    if (!this.content) return false;
    return this.content?.videoId && this.cookiesAccepted && this.lang === 'es';
  }

  get shouldShowImage(): boolean {
    if (!this.content) return false;
    return this.lang === 'en' || (this.lang === 'es' && !this.cookiesAccepted);
  }

  loadYTScript(): void {
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.id = 'youtube-iframe-api';
    script.defer = true;
    body.appendChild(script);
  }

  videoSize(): { height: string; width: string } {
    const width = window.innerWidth > 500 ? 510 : window.innerWidth;
    const height = (width * 9) / 16;
    return { height: `${height}px`, width: `${width}px` };
  }

  private draw() {
    this.changeDetectionRef.detectChanges();
  }

  ngOnInit(): void {
    this.loadYTScript();
    this.content = contentMap[this.contentType?.toLowerCase()];
    this.cookiesAccepted = this.checkCookiesAccepted();
  }

  private checkCookiesAccepted(): boolean {
    const cookieValue = this.getCookieValue('cookieyes-consent');
    if (!cookieValue) return false;

    if (this.cookieIncludes(cookieValue, ['consent:yes'])) return true;
    if (this.cookieIncludes(cookieValue, ['consent:no'])) return false;

    const requiredYoutubeCategories = [
      'analytics:yes',
      'performance:yes',
      'advertisement:yes',
      'functional:yes',
    ];

    const allAccepted = requiredYoutubeCategories.every((c) =>
      cookieValue.includes(c)
    );
    return allAccepted;
  }

  private cookieIncludes(value: string, patterns: string[]): boolean {
    return patterns.some((p) => value.includes(p));
  }

  private getCookieValue(name: string): string | null {
    const match = document.cookie.match(new RegExp(`${name}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  showSecondaryButton(content: EmptyStateContent): boolean {
    if (!content?.secondButton) {
      return false;
    }
    if (!content?.hideSecondaryOnMobile) {
      return true;
    }
    return content?.hideSecondaryOnMobile && !this.isMobile;
  }

  onButton1Click(): void {
    this.firstButtonClick.emit();
  }

  onButton2Click(): void {
    this.secondButtonClick.emit();
  }
}
